import React, { useCallback, useState } from 'react';
import type { ColDef, EditableCallbackParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  LopHistoryDateFilter,
  isoToday,
  isoTwoWeeksAgo,
} from '../../components/LopHistoryDateFilter';
import {
  getIdxCompositionsHistory,
  saveIdxCompositionsHistoryBatch,
} from '../../api/idxCompositionsHistory';
import type { IdxCompositionHistory } from '../../types/IdxCompositionHistory';
import { t } from '../../i18n/t';

// "הרכב מדדים היסטורי" — composite-PK (idxId, assetId, dDate). Mirrors
// IdxRatesHistoryPage's structure exactly (dDate first, sorted desc); the
// same draft/active date-state recipe as every other history screen.
const editableOnAdd = (params: EditableCallbackParams<IdxCompositionHistory>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

const columnDefs: ColDef<IdxCompositionHistory>[] = [
  {
    field: 'dDate',
    headerName: t('idxCompositionsHistory.columns.dDate'),
    width: 140,
    editable: editableOnAdd,
    sort: 'desc',
  },
  {
    field: 'idxId',
    headerName: t('idxCompositionsHistory.columns.idxId'),
    width: 130,
    editable: editableOnAdd,
    cellDataType: 'number',
  },
  {
    field: 'assetId',
    headerName: t('idxCompositionsHistory.columns.assetId'),
    width: 150,
    editable: editableOnAdd,
    cellDataType: 'number',
  },
  {
    field: 'assetPercent',
    headerName: t('idxCompositionsHistory.columns.assetPercent'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'factor',
    headerName: t('idxCompositionsHistory.columns.factor'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
];

const newIdxCompositionHistoryDefaults = (): Partial<IdxCompositionHistory> => ({
  idxId: 0,
  assetId: 0,
  dDate: isoToday(),
  assetPercent: null,
  factor: null,
});

export function IdxCompositionsHistoryPage() {
  const [draftFrom, setDraftFrom] = useState<string>(isoTwoWeeksAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);

  const getRows = useCallback(
    () => getIdxCompositionsHistory(activeFrom, activeTo),
    [activeFrom, activeTo],
  );
  const saveBatch = useCallback(
    (
      added: IdxCompositionHistory[],
      modified: IdxCompositionHistory[],
      deletedIdKeys: unknown[],
    ) =>
      saveIdxCompositionsHistoryBatch(
        added,
        modified,
        deletedIdKeys as string[],
        activeFrom,
        activeTo,
      ),
    [activeFrom, activeTo],
  );

  function handleApply() {
    setActiveFrom(draftFrom);
    setActiveTo(draftTo);
    setFetched(true);
  }

  return (
    <div className="lop-history-page">
      <LopHistoryDateFilter
        fromDate={draftFrom}
        toDate={draftTo}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={handleApply}
      />
      <LopCrudPage<IdxCompositionHistory>
        awaitingFetch={!fetched}
        resourceCode="idxCompositionsHistory"
        titleKey="idxCompositionsHistory.pageTitle"
        idField="idKey"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newIdxCompositionHistoryDefaults}
      />
    </div>
  );
}
